import type { TermCards } from "../../ontology";

/**
 * How a link in a Run's text shows (Johannes, 4 Nov: "we should not assume things that aren't
 * there yet"): `term`, a term the data model knows at the Run's version, opened in DMW's own
 * term card; `unvouched`, an address nothing says leads anywhere, shown as plain text; `web`, an
 * ordinary link. Built provisionally (the orchestrator, 4 Nov 16:21, option 2): no model data
 * declares a namespace such as a project's own unregistered extension, so an address the agent wrote bare that isn't
 * a known term is unvouched wherever it points, and a bare docs address loses its click. Once the
 * terms listing serves the model's declared namespaces, the rule becomes "an unknown address in a
 * known namespace is plain text", and bare docs addresses are links again.
 */
export type RunLinkKind = "term" | "unvouched" | "web";

/** The model's namespaces, from its terms' IRIs: read once per set of term cards. */
const namespacesOf = new WeakMap<TermCards, ReadonlySet<string>>();

/** An IRI's namespace: everything up to its last `#` or `/`. */
function namespaceOf(iri: string): string {
  return iri.slice(0, Math.max(iri.lastIndexOf("#"), iri.lastIndexOf("/")) + 1);
}

function modelNamespaces(cards: TermCards): ReadonlySet<string> {
  let namespaces = namespacesOf.get(cards);
  if (namespaces === undefined) {
    namespaces = new Set([...cards.keys()].map(namespaceOf));
    namespacesOf.set(cards, namespaces);
  }
  return namespaces;
}

/**
 * `cards`: the data model's terms at the Run's version; `null` while they load or when they
 * can't be read, when nothing is vouched for (Sol round 1, S2). `bare`: the text is the address
 * itself, as an autolink writes it. An anchor in the text itself, such as a heading's own, is
 * always a link.
 */
export function runLinkKind(href: string, cards: TermCards | null, bare: boolean): RunLinkKind {
  if (href.startsWith("#")) return "web";
  if (cards === null) return "unvouched";
  if (cards.has(href)) return "term";
  if (bare) return "unvouched";
  const namespace = namespaceOf(href);
  return namespace !== "" && modelNamespaces(cards).has(namespace) ? "unvouched" : "web";
}
